import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../index';
import {
  postLocationTypes,
  rateTypes,
  sellerTypes,
  serviceProviderTypes,
} from '../../helpers';
import { IUser } from '../slices/authStore/authSlice';
import { IPostFile, QueryParams, FilterParams } from 'models';
import { IBaseCategory } from './categoryManagementService';
const { REACT_APP_API_URL } = process.env;

type PriceType = 'price' | 'free' | 'barter';

interface ICharacteristic {
  id: number;
  name: string;
  value: string;
}

export interface IPost {
  id: number;
  title: string;
  category_id: number;
  price: number | null;
  price_type: PriceType;
  seller_type: keyof typeof sellerTypes;
  description: string;
  link: string | null;
  email: string;
  phone_number: string;
  location_type: keyof typeof postLocationTypes;
  zip_code: string;
  city: string;
  state: string;
  user_id: number;
  view_count: number;
  featured: boolean;
  autorenew_duration: 'week' | 'month' | 'annual' | null;
  active: boolean;
  activated_at: Date;
  created_at: Date;
  updated_at: Date;
  files?: Array<IPostFile>;
  characteristics: Array<ICharacteristic>;
  category?: IBaseCategory;
  sub_category_id?: number;
  subCategory?: IBaseCategory;
  savedPosts?: Array<IPost>;
  user?: IUser;
  work_start?: string;
  work_end?: string;
  full_week_available?: boolean;
  rate_type?: keyof typeof rateTypes;
  work_scope?: string;
  service_provider_type?: keyof typeof serviceProviderTypes;
  company_name?: string;
  type?: string;
  working_days?: string;
}

export interface IPostsResponse {
  rows: Array<IPost>;
  count: number;
}

export const postManagementApi = createApi({
  reducerPath: 'postManagementApi',
  tagTypes: ['Posts', 'SavedPosts', 'RecentPosts', 'Services'],
  baseQuery: fetchBaseQuery({
    baseUrl: REACT_APP_API_URL,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState;
      const token = state.auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getPosts: builder.query<IPostsResponse, QueryParams>({
      query: (params) => ({
        url: 'api/posts',
        method: 'GET',
        params,
      }),
      providesTags: ['Posts'],
    }),
    getServices: builder.query<IPostsResponse, QueryParams>({
      query: (params) => ({
        url: 'api/services',
        method: 'GET',
        params,
      }),
      providesTags: ['Services'],
    }),
    getUserPosts: builder.query<IPostsResponse, QueryParams>({
      query: (params) => ({
        url: '/api/user-posts',
        method: 'GET',
        params,
      }),
      providesTags: ['Posts'],
    }),
    getSellerPosts: builder.query<
      IPostsResponse,
      {
        sellerId: Number;
        pagination: QueryParams;
      }
    >({
      query: ({ sellerId, pagination }) => {
        const filterParams = new URLSearchParams();
        if (pagination.filter) {
          Object.entries(pagination.filter as FilterParams).forEach(
            ([key, value]) => {
              if (value !== undefined && value !== '') {
                filterParams.append(key, value.toString());
              }
            }
          );
        }
        const queryString = filterParams.toString().trim();
        return {
          url: `/api/seller-posts/${sellerId}?page=${pagination.page}&limit=${pagination.limit}&${queryString}`,
          method: 'GET',
        };
      },
      providesTags: ['Posts'],
    }),
    getPost: builder.query<IPost, number>({
      query: (postId) => ({
        url: `api/posts/${postId}`,
        method: 'GET',
      }),
      providesTags: ['Posts'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled; // Wait for the query to complete
        // Trigger invalidation of 'RecentPosts' tags
        dispatch(postManagementApi.util.invalidateTags(['RecentPosts']));
      },
    }),
    getPostNextPrev: builder.query<
      {
        prevPost: number | null;
        nextPost: number | null;
      },
      number
    >({
      query: (postId) => ({
        url: `api/posts/next-prev/${postId}`,
        method: 'GET',
      }),
      providesTags: ['Posts'],
    }),
    createPost: builder.mutation({
      query: (body) => ({
        url: 'api/post',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Posts'],
    }),
    updatePost: builder.mutation({
      query: ({ id, body }) => ({
        url: 'api/post/' + id,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Posts'],
    }),
    getSavedPosts: builder.query<IPostsResponse, QueryParams>({
      query: (params) => ({
        url: 'api/saved-posts',
        method: 'GET',
        params,
      }),
      providesTags: ['SavedPosts'],
    }),
    getSavedPostIds: builder.query<Array<{ post_id: number }>, void>({
      query: () => ({
        url: 'api/saved-post-ids',
        method: 'GET',
      }),
      providesTags: ['SavedPosts'],
    }),
    savePost: builder.mutation({
      query: (postId) => ({
        url: 'api/save-post',
        method: 'POST',
        body: {
          postId,
        },
      }),
      invalidatesTags: ['SavedPosts'],
    }),
    deleteSavedPost: builder.mutation({
      query: (postId) => ({
        url: `api/saved-post/${postId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SavedPosts'],
    }),
    getRecentPosts: builder.query<IPostsResponse, QueryParams>({
      query: (params) => ({
        url: 'api/recent-posts',
        method: 'GET',
        params,
      }),
      providesTags: ['RecentPosts'],
    }),
    deleteAllRecentPosts: builder.mutation<any, void>({
      query: () => ({
        url: `api/recent-posts`,
        method: 'DELETE',
      }),
      invalidatesTags: ['RecentPosts'],
    }),
    deleteRecentPost: builder.mutation({
      query: (recentPostId) => ({
        url: `api/recent-post/${recentPostId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['RecentPosts'],
    }),
    getFeaturedPosts: builder.query<Array<IPost>, void>({
      query: () => ({
        url: 'api/featured-posts',
        method: 'GET',
      }),
      providesTags: ['Posts'],
    }),
    getTopRatedPosts: builder.query<Array<IPost>, void>({
      query: () => ({
        url: 'api/toprated-posts',
        method: 'GET',
      }),
      providesTags: ['Posts'],
    }),
    getAllFeaturedPosts: builder.query<IPostsResponse, QueryParams>({
      query: (params) => {
        const filterParams = new URLSearchParams();
        if (params.filter) {
          Object.entries(params.filter as FilterParams).forEach(
            ([key, value]) => {
              if (value !== undefined && value !== '') {
                filterParams.append(key, value.toString());
              }
            }
          );
        }
        const queryString = filterParams.toString().trim();
        return {
          url: `api/all-featured-posts?page=${params.page}&limit=${params.limit}&${queryString}`,
          method: 'GET',
        };
      },
      providesTags: ['Posts'],
    }),
    getFeaturedPostsMaxPrice: builder.query<number, void>({
      query: () => ({
        url: '/api/featured-posts-max-price',
        method: 'GET',
      }),
      providesTags: ['Posts'],
    }),
  }),
});

export const {
  useGetPostsQuery,
  useGetServicesQuery,
  useGetPostQuery,
  useCreatePostMutation,
  useUpdatePostMutation,
  useGetSavedPostsQuery,
  useGetSavedPostIdsQuery,
  useSavePostMutation,
  useDeleteSavedPostMutation,
  useGetRecentPostsQuery,
  useDeleteAllRecentPostsMutation,
  useDeleteRecentPostMutation,
  useGetUserPostsQuery,
  useGetPostNextPrevQuery,
  useGetSellerPostsQuery,
  useLazyGetSellerPostsQuery,
  useGetAllFeaturedPostsQuery,
  useGetFeaturedPostsQuery,
  useGetTopRatedPostsQuery,
  useGetFeaturedPostsMaxPriceQuery,
} = postManagementApi;
